import { createStore } from 'vuex';
import i18n from '../i18n';
import axios from 'axios';
import { BASE_URL } from '/src/Config/config';

export default new createStore({
    state: {
        isAuthenticated: false,
        lastActionTime: null,
        token: sessionStorage.getItem('token') || null,
        tokenExpiry: sessionStorage.getItem('tokenExpiry') || null,
        layout: 'app',
        is_show_sidebar: true,
        is_show_search: false,
        is_dark_mode: false,
        dark_mode: 'light',
        locale: null,
        menu_style: 'vertical',
        layout_style: 'full',
        countryList: [
            { code: 'zh', name: 'Chinese' },
            { code: 'da', name: 'Danish' },
            { code: 'en', name: 'English' },
            { code: 'fr', name: 'French' },
            { code: 'de', name: 'German' },
            { code: 'el', name: 'Greek' },
            { code: 'hu', name: 'Hungarian' },
            { code: 'it', name: 'Italian' },
            { code: 'ja', name: 'Japanese' },
            { code: 'pl', name: 'Polish' },
            { code: 'pt', name: 'Portuguese' },
            { code: 'ru', name: 'Russian' },
            { code: 'es', name: 'Spanish' },
            { code: 'sv', name: 'Swedish' },
            { code: 'tr', name: 'Turkish' },
        ],
        user: (() => {
            try {
                return JSON.parse(sessionStorage.getItem('user')) || null;
            } catch (e) {
                console.warn('Invalid JSON in sessionStorage for user');
                return null;
            }
        })(),
        currentCompany: (() => {
            try {
                return JSON.parse(sessionStorage.getItem('currentCompany')) || null;
            } catch (e) {
                console.warn('Invalid JSON in sessionStorage for currentCompany');
                return null;
            }
        })(),

        userCompanies: (() => {
            try {
                return JSON.parse(sessionStorage.getItem('userCompanies')) || [];
            } catch (e) {
                console.warn('Invalid JSON in sessionStorage for userCompanies');
                return [];
            }
        })(),
        userPermissions: (() => {
            try {
                return JSON.parse(sessionStorage.getItem('userPermissions')) || [];
            } catch (e) {
                console.warn('Invalid JSON in sessionStorage for userPermissions');
                return [];
            }
        })(),
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
            sessionStorage.setItem('user', JSON.stringify(user));
        },

        setAuthData(state, { token, user }) {
            state.token = token;
            state.isAuthenticated = true;
            sessionStorage.setItem('token', token);

            // Store user data
            state.user = user;
            sessionStorage.setItem('user', JSON.stringify(user));

            // Store company data
            state.currentCompany = user.companies.find(c => c.id === user.current_company_id) || null;
            state.userCompanies = user.companies || [];

            // Store permissions
            state.userPermissions = user.permissions || [];
            sessionStorage.setItem('userPermissions', JSON.stringify(user.permissions));

            sessionStorage.setItem('currentCompany', JSON.stringify(state.currentCompany));
            sessionStorage.setItem('userCompanies', JSON.stringify(state.userCompanies));
        },

        setAuthToken(state, token) {
            state.token = token;
            state.isAuthenticated = true;
            sessionStorage.setItem('auth_token', token);
            // Set token expiry (e.g., 1 hour from now)
            const expiry = new Date().getTime() + (60 * 60 * 1000);
            state.tokenExpiry = expiry;
            sessionStorage.setItem('tokenExpiry', expiry);
        },

        setUserPermissions(state, permissions) {
            state.userPermissions = permissions;
            sessionStorage.setItem('userPermissions', JSON.stringify(permissions));
        },
        clearUserPermissions(state) {
            state.userPermissions = [];
            sessionStorage.removeItem('userPermissions');
        },

        setLayout(state, payload) {
            state.layout = payload;
        },
        toggleSideBar(state, value) {
            state.is_show_sidebar = value;
        },
        toggleSearch(state, value) {
            state.is_show_search = value;
        },
        toggleLocale(state, value) {
            value = value || 'en';
            i18n.global.locale = value;
            localStorage.setItem('i18n_locale', value);
            state.locale = value;
        },

        toggleDarkMode(state, value) {
            //light|dark|system
            value = value || 'light';
            localStorage.setItem('dark_mode', value);
            state.dark_mode = value;
            if (value == 'light') {
                state.is_dark_mode = false;
            } else if (value == 'dark') {
                state.is_dark_mode = true;
            } else if (value == 'system') {
                if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                    state.is_dark_mode = true;
                } else {
                    state.is_dark_mode = false;
                }
            }

            if (state.is_dark_mode) {
                document.querySelector('body').classList.add('dark');
            } else {
                document.querySelector('body').classList.remove('dark');
            }
        },

        toggleMenuStyle(state, value) {
            //horizontal|vertical|collapsible-vertical
            value = value || '';
            localStorage.setItem('menu_style', value);
            state.menu_style = value;
            if (!value || value === 'vertical') {
                state.is_show_sidebar = true;
            } else if (value === 'collapsible-vertical') {
                state.is_show_sidebar = false;
            }
        },

        toggleLayoutStyle(state, value) {
            //boxed-layout|large-boxed-layout|full
            value = value || '';
            localStorage.setItem('layout_style', value);
            state.layout_style = value;
        },

        setToken(state, token) {
            state.token = token;
            state.isAuthenticated = !!token;
            sessionStorage.setItem('token', token);
        },
        setTokenExpiry(state, expiry) {
            state.tokenExpiry = expiry;
            sessionStorage.setItem('tokenExpiry', expiry);
        },
        logout(state) {
            state.token = null;
            state.tokenExpiry = null;
            state.isAuthenticated = false;
            state.user = null;
            state.userPermissions = [];
            state.currentCompany = null;
            state.userCompanies = [];
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('tokenExpiry');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('userPermissions');
            sessionStorage.removeItem('currentCompany');
            sessionStorage.removeItem('userCompanies');
        },
        updateLastActionTime(state) {
            state.lastActionTime = new Date().getTime();
        },
        setCurrentCompany(state, company) {
            state.currentCompany = company;
            sessionStorage.setItem('currentCompany', JSON.stringify(company));
        },
        setUserCompanies(state, companies) {
            state.userCompanies = companies;
            sessionStorage.setItem('userCompanies', JSON.stringify(companies));
        },
        clearCompanyData(state) {
            state.currentCompany = null;
            state.userCompanies = [];
            sessionStorage.removeItem('currentCompany');
            sessionStorage.removeItem('userCompanies');
        }
    },
    getters: {
        // layout(state) {
        //     return state.layout;
        // },
        layout: (state) => state.layout || 'app',

        isAuthenticated: (state) => !!state.token,

        isTokenExpired: (state) => {
            if (!state.tokenExpiry) return true;
            return new Date().getTime() > parseInt(state.tokenExpiry);
        },

        currentCompany: (state) => state.currentCompany,
        userCompanies: (state) => state.userCompanies,
        userPermissions: (state) => state.userPermissions,
        hasPermission: (state) => (permission) => {
            return state.userPermissions.includes(permission);
        }
    },
    actions: {
        async login({ commit }, { companyId, email, password }) {
            try {
                const response = await axios.post(`${BASE_URL}/auth/login`, {
                    company_id: companyId,
                    email,
                    password
                });

                console.log('Received response:', response);
                console.log('Response data:', response.data);

                if (response.data.code === 200) {
                    // commit('permissions', response.data.permissions);
                    commit('setAuthToken', response.data.token);
                    commit('setUser', response.data.user);
                    commit('setCurrentCompany', response.data.user.current_company_id);
                    commit('setUserCompanies', response.data.user.companies);
                    commit('setAuthData', {
                        token: response.data.token,
                        user: response.data.user
                    });
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Login error:', error);
                throw error;
            }
        },

        async switchCompany({ commit, state }, companyId) {
            try {
                const response = await axios.post(`${BASE_URL}/auth/switch-company`, {
                    company_id: companyId
                }, {
                    headers: {
                        Authorization: `Bearer ${state.token}`
                    }
                });

                if (response.data.code === 200) {
                    commit('setCurrentCompany', response.data.current_company);
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Company switch error:', error);
                throw error;
            }
        },

        updateLastActionTime({ commit, dispatch }) {
            const tokenExpiry = new Date().getTime() + 300000;
            commit('setTokenExpiry', tokenExpiry);
            commit('updateLastActionTime');
            dispatch('scheduleTokenCheck');
        },

        authenticate({ commit, dispatch }, token) {
            commit('setToken', token);
            dispatch('updateLastActionTime');
        },

        scheduleTokenCheck({ dispatch, getters }) {
            setTimeout(() => {
                if (getters.isTokenExpired) {
                    dispatch('logout');
                } else {
                    dispatch('scheduleTokenCheck');
                }
            }, 60000);
        },

        checkAuthentication({ getters, dispatch }) {
            if (getters.isAuthenticated && getters.isTokenExpired) {
                dispatch('logout');
            }
        },

        logout({ commit }) {
            commit('setAuthToken', null);
            commit('setUser', null);
            commit('clearCompanyData');
            sessionStorage.removeItem('auth_token');
            sessionStorage.removeItem('tokenExpiry');
            sessionStorage.removeItem('user');
        }
    },
    modules: {},
});